// src/components/dashboard/StatisticsOverview.tsx
import React from 'react';
import { useGetUserStatisticsQuery } from '../../store/services/api';
import { Loader2, Calendar, Image, Database, Users, Clock, ChevronRight, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import { Link } from 'react-router-dom';

const StatisticsOverview = () => {
  const { data: statistics, isLoading, error } = useGetUserStatisticsQuery();

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Unknown date';
    }
  };

  const formatFileSize = (sizeInMB: number = 0) => {
    if (sizeInMB < 1) {
      return `${Math.round(sizeInMB * 1000)} KB`;
    }
    return `${sizeInMB.toFixed(2)} MB`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 text-[#FF4444] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4">
        Failed to load statistics
      </div>
    );
  }

  if (!statistics) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Statistics Overview */}
      <div className="bg-white rounded-lg border border-gray-100">
        <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-100">
          <div className="flex items-center mb-2 sm:mb-0">
            <Calendar className="w-4 h-4 text-[#FF4444] mr-2" />
            <span className="text-xs sm:text-sm font-medium">Statistics Overview</span>
          </div>
          <div className="flex items-center">
            <div className="flex items-center text-gray-500 text-xs">
              <Clock className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">Last Updated:</span> {statistics.last_updated ? formatDate(statistics.last_updated) : '-'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4">
          <div className="p-3 sm:p-4 border-r border-b border-gray-100">
            <div className="flex items-center text-xs text-gray-500 mb-1">
              <Calendar className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
              Total Events
            </div>
            <div className="flex items-baseline">
              <span className="text-xl sm:text-2xl font-bold mr-2">{statistics.created_events}</span>
            </div>
            <div className="text-xs text-gray-400">all time</div>
          </div>

          <div className="p-3 sm:p-4 border-b sm:border-r border-gray-100">
            <div className="flex items-center text-xs text-gray-500 mb-1">
              <Calendar className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
              Active Events
            </div>
            <div className="text-xl sm:text-2xl font-bold">{statistics.active_events}</div>
            <div className="text-xs text-gray-400">currently active</div>
          </div>

          <div className="p-3 sm:p-4 border-r border-b border-gray-100">
            <div className="flex items-center text-xs text-gray-500 mb-1">
              <Trash2 className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
              Deleted Events
            </div>
            <div className="text-xl sm:text-2xl font-bold">{statistics.deleted_events}</div>
            <div className="text-xs text-gray-400">all time</div>
          </div>

          <div className="p-3 sm:p-4 border-b border-gray-100">
            <div className="flex items-center text-xs text-gray-500 mb-1">
              <Image className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
              Total Photos
            </div>
            <div className="text-xl sm:text-2xl font-bold">{statistics.uploaded_photos}</div>
            <div className="text-xs text-gray-400">all albums</div>
          </div>

          <div className="p-3 sm:p-4 border-r border-gray-100">
            <div className="flex items-center text-xs text-gray-500 mb-1">
              <Image className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
              Your Uploads
            </div>
            <div className="text-xl sm:text-2xl font-bold">{statistics.photos_by_owner}</div>
            <div className="text-xs text-gray-400">by you</div>
          </div>

          <div className="p-3 sm:p-4 border-r border-gray-100">
            <div className="flex items-center text-xs text-gray-500 mb-1">
              <Users className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
              Guest Uploads
            </div>
            <div className="text-xl sm:text-2xl font-bold">{statistics.photos_by_guests}</div>
            <div className="text-xs text-gray-400">by event guests</div>
          </div>

          <div className="p-3 sm:p-4 border-r border-gray-100">
            <div className="flex items-center text-xs text-gray-500 mb-1">
              <Database className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
              Storage Used
            </div>
            <div className="text-xl sm:text-2xl font-bold">{formatFileSize(statistics.storage_used_mb)}</div>
            <div className="text-xs text-gray-400">{statistics.usage_percentage}% used</div>
          </div>

          <div className="p-3 sm:p-4">
            <div className="flex items-center text-xs text-gray-500 mb-1">
              <Image className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
              Photos Remaining
            </div>
            <div className="text-xl sm:text-2xl font-bold">{statistics.remaining_photo_quota}</div>
            <div className="text-xs text-gray-400">in quota</div>
          </div>
        </div>
      </div>

      {/* Event Breakdown */}
      {statistics.statistics_by_event && statistics.statistics_by_event.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-100">
          <div className="p-3 sm:p-4 flex items-center border-b border-gray-100">
            <Calendar className="w-4 h-4 text-[#FF4444] mr-2" />
            <span className="text-xs sm:text-sm font-medium">Events Breakdown</span>
          </div>

          <div className="p-2 sm:p-4">
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Title</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photos</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Your Uploads</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guest Uploads</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {statistics.statistics_by_event.map((event) => (
                    <tr key={event.event_id} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">{event.title}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-600">{event.photo_count}</td>
                      <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-600">{event.owner_uploads}</td>
                      <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-600">{event.guest_uploads}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-right text-xs sm:text-sm">
                        <Link to={`/dashboard/events/${event.event_id}`}>
                          <Button variant="outline" className="h-7 sm:h-8 text-xs flex items-center px-2 sm:px-3">
                            View
                            <ChevronRight className="w-3 sm:w-3.5 h-3 sm:h-3.5 ml-1" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsOverview;